import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Gauge, Loader2, RefreshCw, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import useIsMobile from "../hooks/useIsMobile";
import { PRESETS, canExportCockpit, cockpitError, presetRange, rangeProblem, readHidden, toggleHidden, writeHidden } from "../lib/cockpit";
import { Exceptions, Highlights, SectionCard } from "../components/cockpit/CockpitBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../components/ui/responsive-popover";

const hourText = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

export default function Cockpit() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(() => presetRange("month"));
  const [state, setState] = useState({ phase: "loading" });
  const [dictionary, setDictionary] = useState(null);
  const [hidden, setHidden] = useState(readHidden);
  const [exporting, setExporting] = useState(false);
  const problem = rangeProblem(range);

  const load = useCallback(async () => {
    if (rangeProblem(range)) return;
    setState((prev) => (prev.data ? { ...prev, refreshing: true } : { phase: "loading" }));
    try {
      const [overview, dict] = await Promise.all([api.get("/cockpit/overview", { params: range }), dictionary ? Promise.resolve({ data: dictionary }) : api.get("/cockpit/dictionary")]);
      setDictionary(dict.data);
      setState({ phase: "ready", data: overview.data });
    } catch (e) {
      setState((prev) => ({ phase: "error", message: cockpitError(e, "Impossible de charger le Cockpit. Réessayez dans quelques instants."), data: prev.data }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const choose = (value) => {
    setPreset(value);
    if (value !== "custom") setRange(presetRange(value));
  };
  const kpiById = useMemo(() => Object.fromEntries((dictionary?.sections || []).flatMap((s) => s.kpis.map((k) => [k.id, k]))), [dictionary]);
  const sections = state.data?.sections || [];
  const shown = sections.filter((s) => !hidden.includes(s.id));
  const fold = (id) => { const next = toggleHidden(hidden, id); setHidden(next); writeHidden(next); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/cockpit/export.csv", { params: range, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "jamora-cockpit.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Vous n'avez pas accès à cet export." : "Impossible de télécharger l'export. Réessayez dans quelques instants.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="cockpit-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Cockpit de direction</h1>
          <p className="text-sm text-slate-500">La vue d'entreprise : chaque chiffre vient du module qui le possède, avec sa définition. Aucune donnée n'est inventée.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {sections.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" data-testid="cockpit-sections-menu"><SlidersHorizontal size={16} className="mr-1.5" aria-hidden="true" />Domaines ({shown.length}/{sections.length})</Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 space-y-2" align="end">
                <p className="text-xs text-slate-500">Masquer un domaine se mémorise dans ce navigateur.</p>
                {sections.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm"><Checkbox checked={!hidden.includes(s.id)} onCheckedChange={() => fold(s.id)} data-testid={`cockpit-fold-${s.id}`} />{s.label}</label>
                ))}
              </PopoverContent>
            </Popover>
          )}
          {canExportCockpit(user) && (
            <Button type="button" variant="outline" onClick={exportCsv} disabled={exporting || !!problem} data-testid="cockpit-export">
              {exporting ? <Loader2 size={16} className="mr-1.5 animate-spin" aria-hidden="true" /> : <Download size={16} className="mr-1.5" aria-hidden="true" />}Exporter (CSV)
            </Button>
          )}
        </div>
      </div>

      <Card className="p-3" data-testid="cockpit-period">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56 max-w-full">
            <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="cockpit-preset">Période</label>
            <Select value={preset} onValueChange={choose}>
              <SelectTrigger id="cockpit-preset" data-testid="cockpit-preset"><SelectValue /></SelectTrigger>
              <SelectContent>{PRESETS.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {preset === "custom" && (
            <>
              <div><label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="cockpit-from">Du</label>
                <Input id="cockpit-from" type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} data-testid="cockpit-from" /></div>
              <div><label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="cockpit-to">Au</label>
                <Input id="cockpit-to" type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} data-testid="cockpit-to" /></div>
            </>
          )}
          <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
            {state.data && <span data-testid="cockpit-as-of">Actualisé à {hourText(state.data.as_of)}</span>}
            <Button type="button" variant="outline" size="sm" onClick={load} disabled={!!problem || state.phase === "loading" || state.refreshing} data-testid="cockpit-refresh">
              <RefreshCw size={14} className={`mr-1.5 ${state.refreshing ? "animate-spin" : ""}`} aria-hidden="true" />Actualiser
            </Button>
          </div>
        </div>
        {problem && <p className="mt-2 text-sm text-red-700" role="alert" data-testid="cockpit-period-problem">{problem}</p>}
        <p className="mt-2 text-2xs text-slate-500">La période ne touche que les indicateurs « de période » ; les autres disent « aujourd'hui » ou leur fenêtre fixe.</p>
      </Card>

      {state.phase === "loading" && (
        <div className="space-y-3" data-testid="cockpit-loading">
          <Skeleton className="h-24 w-full" /><Skeleton className="h-48 w-full" /><Skeleton className="h-48 w-full" />
        </div>
      )}
      {state.phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="cockpit-error">
          <p className="text-sm text-slate-700">{state.message}</p>
          <Button type="button" onClick={load}>Réessayer</Button>
        </Card>
      )}
      {state.phase === "ready" && sections.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="cockpit-empty">
          <Gauge size={28} className="mx-auto text-slate-400" aria-hidden="true" />
          <p className="text-sm font-medium text-slate-900">Aucun domaine à afficher</p>
          <p className="text-sm text-slate-600">Le Cockpit montre un domaine seulement si votre rôle donne aussi le droit « tout voir » de son module. Demandez-le à un administrateur.</p>
        </Card>
      )}
      {state.phase === "ready" && sections.length > 0 && (
        <>
          <Highlights sections={sections} kpiById={kpiById} />
          <Exceptions items={state.data.exceptions} />
          {shown.length === 0 && <Card className="p-4 text-sm text-slate-600" data-testid="cockpit-all-hidden">Tous les domaines sont masqués : utilisez « Domaines » pour en afficher.</Card>}
          {shown.map((section) => <SectionCard key={section.id} section={section} kpiById={kpiById} period={state.data.period} version={state.data.dictionary_version} isMobile={isMobile} />)}
        </>
      )}
    </div>
  );
}
